import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiCheck, FiX, FiLoader, FiSettings, FiBriefcase } from 'react-icons/fi';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';

const theme = {
  colors: {
    primary: '#875A7B',
    accent: '#9C7A97',
    light: '#F8FAFC',
    danger: '#EF4444',
    success: '#10B981',
    border: '#E2E8F0',
    text: '#1E293B',
    textSecondary: '#64748B'
  },
  styles: {
    button: 'inline-flex items-center px-4 py-2 rounded-lg shadow-sm transition-all font-medium hover:shadow-md',
    input: 'w-full px-4 py-2.5 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-terrecuite-500',
    tableHeader: 'bg-gradient-to-r from-terrecuite-600 to-terrecuite-800',
    tableRowHover: 'hover:bg-terrecuite-50 transition-colors'
  }
};

export default function ParamFonctionnelsOrgas() {
  const [fonctionnels, setFonctionnels] = useState([]);
  const [selectedFonctionnel, setSelectedFonctionnel] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [assignedOrgas, setAssignedOrgas] = useState([]);
  const [loading, setLoading] = useState({ table: true, modal: false });
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    api.get('/utilisateurs/fonctionnels')
      .then(res => {
        setFonctionnels(res.data.fonctionnelle);
        setLoading(prev => ({ ...prev, table: false }));
      })
      .catch(() => {
        setError("Erreur de chargement des fonctionnels");
        setLoading(prev => ({ ...prev, table: false }));
      });
  }, []);

  const loadOrganisations = async (fonctionnelId) => {
    try {
      setLoading(prev => ({ ...prev, modal: true }));
      const [allOrgas, assigned] = await Promise.all([
        api.get('/organisations'),
        api.get(`/organisations/fonctionnel/${fonctionnelId}`)
      ]);
      
      setOrganisations(allOrgas.data.organisations);
      setAssignedOrgas(assigned.data.organisations.map(org => org.id));
    } catch (error) {
      setError("Erreur de chargement des données");
    } finally {
      setLoading(prev => ({ ...prev, modal: false }));
    }
  };

  const handleSelectFonctionnel = (id) => {
    setSelectedFonctionnel(id);
    setShowModal(true);
    loadOrganisations(id);
  };

  const handleCheckboxChange = (orgId) => {
    setAssignedOrgas(prev => 
      prev.includes(orgId) 
        ? prev.filter(id => id !== orgId) 
        : [...prev, orgId]
    );
  };

  const handleSave = async () => {
    try {
      await api.post(
        `/parametrages/fonctionnels/${selectedFonctionnel}/organisations`,
        { organisation_ids: assignedOrgas }
      );
      toast.success("Affectations enregistrées avec succès");
  
      // Mise à jour locale du state `fonctionnels`
      setFonctionnels(prev =>
        prev.map(f =>
          f.utilisateur_id === selectedFonctionnel
            // On remplace son tableau d'organisations par celles qu'on vient d'assigner
            ? {
                ...f,
                organisations: organisations.filter(org =>
                  assignedOrgas.includes(org.id)
                )
              }
            : f
        )
      );
  
      setShowModal(false);
    } catch (error) {
      setError("Erreur lors de la mise à jour");
      toast.error("Une erreur est survenue");
    }
  };
  

  const TableSkeleton = () => (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />
      ))}
    </div>
  );

  return (
    <div className="p-8 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FiSettings className="w-8 h-8 text-terrecuite-600" />
              Gestion des Accès Organisationnels
            </h1>
            <p className="mt-2 text-gray-600">Administration des droits d'accès par fonctionnel</p>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-100 border border-red-300 rounded-sm flex items-center gap-3">
            <FiX className="text-red-600 shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {loading.table ? (
            <div className="p-6">
              <TableSkeleton />
            </div>
          ) : (
            <table className="w-full">
              <thead className={theme.styles.tableHeader}>
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">Fonctionnel</th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">Contact</th>
                  <th className="px-6 py-4 text-center text-white font-semibold text-sm">Organisations</th>
                  <th className="px-6 py-4 text-right text-white font-semibold text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {fonctionnels.map((f) => (
                  <motion.tr
                    key={f.utilisateur_id}
                    whileHover={{ scale: 0.99 }}
                    className={theme.styles.tableRowHover}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-terrecuite-100 rounded-lg flex items-center justify-center">
                          <FiUser className="text-terrecuite-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{f.prenom} {f.nom}</p>
                          <p className="text-sm text-gray-500">{f.poste}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-gray-900">{f.email}</span>
                        <span className="text-sm text-gray-500">{f.telephone}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-2 bg-terrecuite-100 text-terrecuite-700 px-3 py-1 rounded-full">
                        <FiBriefcase />
                        <span>{f.organisations?.length || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleSelectFonctionnel(f.utilisateur_id)}
                        className="text-terrecuite-600 hover:text-terrecuite-800 flex items-center justify-end gap-2"
                      >
                        <FiSettings className="shrink-0" />
                        <span className="hidden md:inline">Gérer les accès</span>
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-xl"
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <FiUser className="text-terrecuite-600" />
                    {fonctionnels.find(f => f.utilisateur_id === selectedFonctionnel)?.prenom}{' '}
                    {fonctionnels.find(f => f.utilisateur_id === selectedFonctionnel)?.nom}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-4 h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-terrecuite-300 scrollbar-track-terrecuite-50">
  {loading.modal ? (
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />
      ))}
    </div>
  ) : (
    <div className="grid gap-2">
      {organisations.map(org => (
        <label
          key={org.id}
          className={`flex items-start p-3 rounded-lg transition-all cursor-pointer ${
            assignedOrgas.includes(org.id)
              ? 'border-l-4 border-terrecuite-500 bg-terrecuite-50'
              : 'border-l-4 border-transparent hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center h-5 mt-1">
            <input
              type="checkbox"
              checked={assignedOrgas.includes(org.id)}
              onChange={() => handleCheckboxChange(org.id)}
              className="w-4 h-4 text-terrecuite-600 border-gray-300 rounded focus:ring-terrecuite-500"
            />
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{org.nomOrganisation}</p>
            <p className="text-xs text-gray-500 truncate">{org.domaine_auth}</p>
          </div>
        </label>
      ))}
    </div>
  )}
</div>

<div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
  <button
    onClick={handleSave}
    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-terrecuite-600 hover:bg-terrecuite-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-terrecuite-500 transition-colors"
  >
    <FiCheck className="mr-2 -ml-1" size={16} />
    Sauvegarder
  </button>
</div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}